import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
     MdCheckCircle, MdTrendingUp,
    MdPeople, MdVerifiedUser, 
    MdBusiness, MdSecurity, MdStar, MdArrowRight
} from 'react-icons/md';
import { FaSeedling, FaArrowRight, FaLeaf, FaHandshake, FaGlobeAmericas } from 'react-icons/fa';
import { GiMilkCarton, GiWheat, GiFertilizerBag } from 'react-icons/gi';
import { RiRobot2Line } from 'react-icons/ri';

const Home = () => {
    const [isMobile, setIsMobile] = useState(false);
    const [isTablet, setIsTablet] = useState(false);

    useEffect(() => {
        const checkViewport = () => {
            const width = window.innerWidth;
            setIsMobile(width <= 768);
            setIsTablet(width > 768 && width <= 1024);
        };

        checkViewport();
        window.addEventListener('resize', checkViewport);

        return () => {
            window.removeEventListener('resize', checkViewport);
        };
    }, []);

    const coreOfferings = [
        {
            icon: <RiRobot2Line />,
            title: 'Agri Robotics',
            description: 'Smart automated machines for precision farming operations.',
            features: ['Automated Harvesting', 'Precision Spraying', 'Soil Monitoring']
        },
        {
            icon: <GiFertilizerBag />,
            title: 'Fertilizer Manufacturing',
            description: 'High-quality organic & chemical fertilizers.',
            features: ['Organic Variants', 'Chemical Blends', 'Soil-Specific']
        },
        {
            icon: <FaGlobeAmericas />,
            title: 'Export Services',
            description: 'Global agri-product sourcing & market access.',
            features: ['1+ Countries', 'Quality Certified', 'Logistics Support']
        },
        {
            icon: <FaHandshake />,
            title: 'Consultancy',
            description: 'Tailored solutions for farms & agribusinesses.',
            features: ['Farm Planning', 'Market Strategy', 'Tech Adoption']
        },
        {
            icon: <FaLeaf />,
            title: 'Organic Products',
            description: 'Fresh, chemical-free produce from farms.',
            features: ['Direct Sourcing', 'Quality Assured', 'Home Delivery']
        },
        {
            icon: <GiMilkCarton />,
            title: 'Dairy Products',
            description: 'Hygienic dairy supply from trusted farms.',
            features: ['Fresh Milk', 'Dairy Products', 'Quality Tested']
        }
    ];

    const stats = [
        {
            number: '1000+',
            label: 'Farmers Empowered',
            description: 'Across India',
            icon: <MdPeople />
        },
        {
            number: '45%+',
            label: 'Yield Improvement',
            description: 'Average increase',
            icon: <MdTrendingUp />
        },
        {
            number: '10+',
            label: 'Districts Covered',
            description: 'With our solutions',
            icon: <GiWheat />
        },
        {
            number: '98%',
            label: 'Success Rate',
            description: 'Farmer satisfaction',
            icon: <MdStar />
        }
    ];

    const values = [
        {
            icon: <RiRobot2Line />,
            title: 'Technology-Driven',
            description: 'Modern tech solutions for efficient farming.'
        },
        {
            icon: <MdVerifiedUser />,
            title: 'Ethical Sourcing',
            description: 'Complete transparency in supply chain.'
        },
        {
            icon: <MdPeople />,
            title: 'Farmer-First',
            description: 'Solutions designed for farmers\' needs.'
        },
        {
            icon: <MdBusiness />,
            title: 'Expert Advisory',
            description: 'Experienced professionals guiding transformation.'
        },
        {
            icon: <FaLeaf />,
            title: 'Sustainable',
            description: 'Eco-friendly long-term practices.'
        },
        {
            icon: <MdSecurity />,
            title: 'End-to-End Support',
            description: 'Comprehensive planning to execution.'
        }
    ];

    return (
        <div className="premium-home">
            {/* SEO Meta Tags - Added for better SEO */}
            <head>
                <title>Krishidnya - Transforming Indian Agriculture with Technology</title>
                <meta name="description" content="Krishidnya empowers farmers with modern technology, smart farming solutions, and comprehensive agricultural services for sustainable and profitable farming." />
                <meta name="keywords" content="agriculture technology, smart farming, farmer empowerment, agricultural solutions, organic farming, dairy products, farm consultancy" />
                <meta property="og:title" content="Krishidnya - Agricultural Technology Solutions" />
                <meta property="og:description" content="Modern agricultural solutions for sustainable farming and farmer prosperity" />
                <link rel="canonical" href="/" />
            </head>

            {/* Hero Section - Fixed header spacing */}
            <header className="hero-section" role="banner">
                <div className="container">
                    <div className="hero-content">
                        <div className="hero-text">
                            <div className="hero-badge">
                                <FaSeedling />
                                <span>Transforming Indian Agriculture</span>
                            </div>
                            
                            <h1 className="hero-title">
                                Empowering Farmers With
                                <span className="title-green"> Technology & Innovation</span>
                            </h1>
                            
                            <p className="hero-description">
                                Krishidnya combines traditional farming wisdom with modern technology to create sustainable, profitable, and resilient agricultural ecosystems. Our solutions empower farmers with data-driven insights, market access, and smart farming tools.
                            </p>
                            
                            <div className="hero-actions">
                                <Link to="/contact" className="btn-primary" aria-label="Start your agricultural journey with Krishidnya">
                                    <span>Start Your Journey</span>
                                    <FaArrowRight className="btn-icon" />
                                </Link>
                                <Link to="/about" className="btn-secondary" aria-label="Learn more about Krishidnya">
                                    Learn More About Us
                                </Link>
                            </div>
                        </div>
                        
                        <div className="hero-stats">
                            <div className="stats-grid">
                                {stats.map((stat, index) => (
                                    <div key={index} className="stat-preview">
                                        <div className="stat-preview-icon">
                                            {stat.icon}
                                        </div>
                                        <div className="stat-preview-content">
                                            <div className="stat-preview-number">{stat.number}</div>
                                            <div className="stat-preview-label">{stat.label}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="hero-bottom">
                    <div className="container">
                        <div className="scroll-indicator">
                            <span>Scroll to explore</span>
                            <div className="scroll-line"></div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Solutions Section */}
            <section className="solutions-section" aria-labelledby="solutions-heading">
                <div className="container">
                    <div className="section-intro">
                        <h2 id="solutions-heading">Our Core Solutions</h2>
                        <p className="section-description">
                            Comprehensive services designed to revolutionize modern agriculture with technology and innovation
                        </p>
                    </div>
                    
                    <div className="solutions-grid">
                        {coreOfferings.map((solution, index) => (
                            <article 
                                key={index} 
                                className="solution-card"
                                itemScope
                                itemType="https://schema.org/Service"
                            >
                                <div className="solution-icon" aria-hidden="true">
                                    {solution.icon}
                                </div>
                                <div className="solution-content">
                                    <h3 itemProp="name">{solution.title}</h3>
                                    <p itemProp="description">{solution.description}</p>
                                    <div className="solution-features">
                                        {solution.features.map((feature, idx) => (
                                            <div key={idx} className="feature-item">
                                                <MdCheckCircle className="feature-check" />
                                                <span>{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <Link to="/services" className="solution-link" aria-label={`Learn more about ${solution.title}`}>
                                        Learn More <MdArrowRight />
                                    </Link>
                                </div>
                            </article>
                        ))}
                    </div>
                    
                    <div className="section-cta">
                        <Link to="/services" className="btn-secondary" aria-label="View all agricultural solutions">
                            View All Solutions
                        </Link>
                    </div>
                </div>
            </section>

            {/* Impact Section */}
            <section className="impact-section" aria-labelledby="impact-heading">
                <div className="container">
                    <div className="section-intro">
                        <h2 id="impact-heading">Transforming Agriculture Across India</h2>
                        <p className="section-description">
                            Driving meaningful change and measurable impact across the agricultural landscape
                        </p>
                    </div>
                    
                    <div className="impact-grid" itemScope itemType="https://schema.org/Organization">
                        {stats.map((stat, index) => (
                            <div key={index} className="impact-card">
                                <div className="impact-icon" aria-hidden="true">
                                    {stat.icon}
                                </div>
                                <div className="impact-number">{stat.number}</div>
                                <div className="impact-label">{stat.label}</div>
                                <p className="impact-desc">{stat.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Values Section */}
            <section className="values-section" aria-labelledby="values-heading">
                <div className="container">
                    <div className="section-intro">
                        <h2 id="values-heading">Why Choose Krishidnya</h2>
                        <p className="section-description">
                            The principles that guide our commitment to agricultural excellence and farmer prosperity
                        </p>
                    </div>
                    
                    <div className="values-grid">
                        {values.map((value, index) => (
                            <article key={index} className="value-card">
                                <div className="value-icon" aria-hidden="true">
                                    {value.icon}
                                </div>
                                <h3>{value.title}</h3>
                                <p>{value.description}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="final-cta" aria-labelledby="cta-heading">
                <div className="container">
                    <div className="cta-content">
                        <h2 id="cta-heading">Join the Agricultural Revolution</h2>
                        <p>Connect with us to discover how our technology-driven solutions can transform your farming operations and increase profitability.</p>
                        
                        <div className="cta-actions">
                            <Link to="/contact" className="btn-primary" aria-label="Get started with Krishidnya solutions">
                                Get Started
                            </Link>
                            <Link to="/demo" className="btn-secondary" aria-label="Request a demo of our agricultural solutions">
                                Request Demo
                            </Link>
                        </div>
                        
                        <div className="cta-features">
                            <div className="feature">
                                <MdCheckCircle /> <span>Free Consultation</span>
                            </div>
                            <div className="feature">
                                <MdCheckCircle /> <span>Custom Solutions</span>
                            </div>
                            <div className="feature">
                                <MdCheckCircle /> <span>24/7 Support</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <style jsx>{`
    /* Font Import */
    @import url('https://fonts.googleapis.com/css2?family=Source+Serif+Pro:wght@400;600;700&family=Inter:wght@300;400;500;600;700&display=swap');

    /* Premium Design System - Mobile First */
    :root {
        --primary-black: #000000;
        --secondary-black: #1A1A1A;
        --medium-black: #333333;
        --light-black: #666666;
        --primary-green: #2E8B57;
        --secondary-green: #3A7D34;
        --light-green: #4CAF7A;
        --accent-green: #2D5A27;
        --white: #FFFFFF;
        --off-white: #FAFAFA;
        --light-gray: #F5F5F5;
        --medium-gray: #E8E8E8;
        --border-color: #E0E0E0;
        --text-primary: #000000;
        --text-secondary: #333333;
        --text-light: #666666;
        --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.04);
        --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.08);
        --shadow-lg: 0 8px 24px rgba(0, 0, 0, 0.12);
        --radius-sm: 6px;
        --radius-md: 10px;
        --radius-lg: 16px;
        --radius-xl: 20px;
        --transition: all 0.3s ease;
        --header-height: ${isMobile ? '70px' : '80px'}; /* Added variable for header height */
    }

    * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
    }

    .premium-home {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        color: var(--text-primary);
        background: var(--white);
        line-height: 1.6;
        overflow-x: hidden;
        scroll-behavior: smooth; /* Added for smooth scrolling */
    }

    .container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 ${isMobile ? '20px' : '24px'};
    }

    /* Skip to main content for accessibility */
    .skip-to-content {
        position: absolute;
        top: -40px;
        left: 0;
        background: var(--primary-green);
        color: white;
        padding: 8px;
        z-index: 1001;
        text-decoration: none;
    }
    
    .skip-to-content:focus {
        top: 0;
    }

    /* Typography - Mobile First */
    h1, h2, h3, h4 {
        font-family: 'Source Serif Pro', serif;
        font-weight: 600;
        line-height: 1.2;
        letter-spacing: -0.01em;
        color: var(--primary-black);
    }

    h1 { 
        font-size: ${isMobile ? '2.25rem' : isTablet ? '3rem' : '3.5rem'};
        font-weight: 700;
        letter-spacing: -0.02em;
    }

    h2 { 
        font-size: ${isMobile ? '1.75rem' : isTablet ? '2.25rem' : '2.5rem'};
        margin-bottom: 1rem;
    }

    h3 { 
        font-size: ${isMobile ? '1.25rem' : isTablet ? '1.5rem' : '1.75rem'};
        margin-bottom: 0.75rem;
    }

    p { 
        font-size: ${isMobile ? '1rem' : '1.125rem'};
        color: var(--text-secondary);
        line-height: 1.7;
        font-weight: 400;
    }

    .section-description {
        font-size: ${isMobile ? '1rem' : '1.125rem'};
        color: var(--text-light);
        margin-top: 0.5rem;
        font-weight: 400;
    }

    /* Buttons - Mobile Optimized */
    .btn-primary, .btn-secondary {
        padding: ${isMobile ? '12px 24px' : '14px 32px'};
        font-size: ${isMobile ? '0.95rem' : '1rem'};
        font-family: 'Inter', sans-serif;
        font-weight: 600;
        border-radius: var(--radius-md);
        text-decoration: none;
        transition: var(--transition);
        border: 2px solid transparent;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        text-align: center;
        min-width: ${isMobile ? '140px' : 'auto'};
    }

    .btn-primary {
        background: var(--primary-black);
        color: var(--white);
    }

    .btn-primary:hover, .btn-primary:focus {
        background: var(--secondary-black);
        transform: translateY(-2px);
        box-shadow: var(--shadow-md);
        outline: 2px solid var(--primary-green);
    }

    .btn-secondary {
        background: transparent;
        color: var(--primary-black);
        border: 2px solid var(--primary-black);
    }

    .btn-secondary:hover, .btn-secondary:focus {
        background: var(--primary-black);
        color: var(--white);
        transform: translateY(-2px);
        outline: 2px solid var(--primary-green);
    }

    .btn-icon {
        font-size: ${isMobile ? '1rem' : '1.125rem'};
        transition: transform 0.3s ease;
    }

    .btn-primary:hover .btn-icon, .btn-primary:focus .btn-icon {
        transform: translateX(4px);
    }

    /* Hero Section - Mobile Optimized with proper header spacing */
    .hero-section {
        padding: ${isMobile ? 'calc(var(--header-height) + 40px) 0 40px' : 'calc(var(--header-height) + 60px) 0 60px'}; /* Dynamic padding based on header height */
        background: var(--white);
        position: relative;
        margin-top: 0;
    }

    .hero-section::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(90deg, transparent, var(--border-color), transparent);
    }

    .hero-content {
        display: flex;
        flex-direction: ${isMobile ? 'column' : 'row'};
        gap: ${isMobile ? '40px' : '60px'};
        align-items: center;
        position: relative;
        z-index: 1;
    }

    .hero-text {
        flex: 1;
    }

    .hero-stats {
        flex: ${isMobile ? '0 0 auto' : '1'};
        width: ${isMobile ? '100%' : 'auto'};
    }

    .hero-badge {
        display: inline-flex;
        align-items: center;
        gap: 10px;
        background: rgba(46, 139, 87, 0.1);
        color: var(--primary-green);
        padding: ${isMobile ? '8px 16px' : '10px 20px'};
        border-radius: var(--radius-round);
        font-size: ${isMobile ? '0.85rem' : '14px'};
        font-weight: 600;
        margin-bottom: ${isMobile ? '20px' : '30px'};
        border: 1px solid rgba(46, 139, 87, 0.2);
    }

    .hero-badge svg {
        font-size: ${isMobile ? '0.9rem' : '16px'};
    }

    .hero-title {
        margin-bottom: ${isMobile ? '1rem' : '1.5rem'};
        line-height: 1.1;
    }

    .title-green {
        color: var(--primary-green);
        display: block;
        margin-top: ${isMobile ? '0.5rem' : '0.75rem'};
    }

    .hero-description {
        margin-bottom: ${isMobile ? '2rem' : '3rem'};
        font-size: ${isMobile ? '1.063rem' : '1.125rem'};
        line-height: 1.6;
    }

    .hero-actions {
        display: flex;
        gap: ${isMobile ? '0.75rem' : '1rem'};
        margin-bottom: ${isMobile ? '3rem' : '0'};
        flex-direction: ${isMobile ? 'column' : 'row'};
        align-items: ${isMobile ? 'stretch' : 'flex-start'};
    }

    /* Stats Preview */
    .stats-grid {
        display: grid;
        grid-template-columns: ${isMobile ? '1fr 1fr' : '1fr 1fr'};
        gap: ${isMobile ? '1rem' : '1.5rem'};
        background: var(--off-white);
        padding: ${isMobile ? '1.5rem' : '2rem'};
        border-radius: var(--radius-lg);
        border: 1px solid var(--border-color);
    }

    .stat-preview {
        display: flex;
        align-items: center;
        gap: ${isMobile ? '0.75rem' : '1rem'};
    }

    .stat-preview-icon {
        width: ${isMobile ? '40px' : '48px'};
        height: ${isMobile ? '40px' : '48px'};
        background: var(--white);
        border-radius: var(--radius-md);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${isMobile ? '1.25rem' : '1.5rem'};
        color: var(--primary-green);
        flex-shrink: 0;
        border: 1px solid var(--border-color);
    }

    .stat-preview-content {
        flex: 1;
    }

    .stat-preview-number {
        font-size: ${isMobile ? '1.5rem' : '1.75rem'};
        font-weight: 700;
        color: var(--primary-black);
        margin-bottom: 0.25rem;
        font-family: 'Source Serif Pro', serif;
    }

    .stat-preview-label {
        font-size: ${isMobile ? '0.75rem' : '0.875rem'};
        color: var(--text-light);
        line-height: 1.4;
        font-weight: 500;
    }

    /* Scroll Indicator */
    .hero-bottom {
        padding-top: ${isMobile ? '2rem' : '3rem'};
    }

    .scroll-indicator {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.5rem;
        color: var(--text-light);
        font-size: 0.875rem;
    }

    .scroll-line {
        width: 1px;
        height: 40px;
        background: var(--border-color);
        position: relative;
    }

    .scroll-line::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 20px;
        background: var(--primary-green);
        animation: scroll 2s infinite;
    }

    /* Sections Common */
    section {
        padding: ${isMobile ? '80px 0' : '100px 0'}; /* Increased padding */
        scroll-margin-top: var(--header-height); /* Added for anchor links with fixed header */
    }

    .section-intro {
        text-align: center;
        max-width: 700px;
        margin: 0 auto ${isMobile ? '2rem' : '3rem'};
    }

    .section-cta {
        text-align: center;
        margin-top: ${isMobile ? '2.5rem' : '3rem'};
    }

    /* Solutions Section */
    .solutions-section {
        background: var(--light-gray);
    }

    .solutions-grid {
        display: grid;
        grid-template-columns: ${isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'};
        gap: ${isMobile ? '1.5rem' : '2rem'};
    }

    .solution-card {
        background: var(--white);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        padding: ${isMobile ? '1.5rem 1.25rem' : '2rem 1.75rem'};
        transition: var(--transition);
        display: flex;
        flex-direction: column;
        height: 100%;
    }

    .solution-card:hover, .solution-card:focus-within {
        transform: translateY(-4px);
        box-shadow: var(--shadow-lg);
        border-color: var(--primary-green);
    }

    .solution-icon {
        width: ${isMobile ? '48px' : '56px'};
        height: ${isMobile ? '48px' : '56px'};
        background: linear-gradient(135deg, #2E8B57 0%, #3A7D34 100%);
        border-radius: var(--radius-md);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${isMobile ? '1.25rem' : '1.5rem'};
        color: var(--white);
        margin-bottom: ${isMobile ? '1rem' : '1.25rem'};
    }

    .solution-content {
        flex: 1;
    }

    .solution-content h3 {
        margin-bottom: ${isMobile ? '0.5rem' : '0.75rem'};
    }

    .solution-content p {
        font-size: ${isMobile ? '0.95rem' : '1rem'};
        color: var(--text-light);
        margin-bottom: ${isMobile ? '1rem' : '1.25rem'};
        line-height: 1.6;
    }

    .solution-features {
        margin-bottom: ${isMobile ? '1rem' : '1.25rem'};
    }

    .feature-item {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-bottom: 0.5rem;
        font-size: ${isMobile ? '0.875rem' : '0.95rem'};
        color: var(--text-secondary);
    }

    .feature-check {
        color: var(--primary-green);
        flex-shrink: 0;
        font-size: ${isMobile ? '0.875rem' : '1rem'};
    }

    .solution-link {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        color: var(--primary-green);
        font-weight: 600;
        text-decoration: none;
        font-size: ${isMobile ? '0.875rem' : '1rem'};
        transition: var(--transition);
    }

    .solution-link:hover, .solution-link:focus {
        gap: 1rem;
        outline: none;
        text-decoration: underline;
    }

    /* Impact Section */
    .impact-section {
        background: var(--white);
    }

    .impact-grid {
        display: grid;
        grid-template-columns: ${isMobile ? '1fr 1fr' : 'repeat(4, 1fr)'};
        gap: ${isMobile ? '1rem' : '1.5rem'};
    }

    .impact-card {
        background: var(--off-white);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        padding: ${isMobile ? '1.5rem 1rem' : '2rem 1.5rem'};
        text-align: center;
        transition: var(--transition);
    }

    .impact-card:hover {
        transform: translateY(-4px);
        box-shadow: var(--shadow-md);
        border-color: var(--primary-green);
    }

    .impact-icon {
        font-size: ${isMobile ? '2rem' : '2.5rem'};
        color: var(--primary-green);
        margin-bottom: ${isMobile ? '0.75rem' : '1rem'};
    }

    .impact-number {
        font-size: ${isMobile ? '1.75rem' : '2rem'};
        font-weight: 700;
        color: var(--primary-black);
        margin-bottom: 0.25rem;
        font-family: 'Source Serif Pro', serif;
    }

    .impact-label {
        font-size: ${isMobile ? '0.875rem' : '1rem'};
        font-weight: 600;
        color: var(--text-secondary);
        margin-bottom: 0.25rem;
    }

    .impact-desc {
        font-size: ${isMobile ? '0.75rem' : '0.875rem'};
        color: var(--text-light);
        margin: 0;
    }

    /* Values Section */
    .values-section {
        background: var(--light-gray);
    }

    .values-grid {
        display: grid;
        grid-template-columns: ${isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'};
        gap: ${isMobile ? '1.5rem' : '2rem'};
    }

    .value-card {
        background: var(--white);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        padding: ${isMobile ? '1.5rem 1.25rem' : '2rem 1.75rem'};
        text-align: center;
        transition: var(--transition);
    }

    .value-card:hover {
        transform: translateY(-4px);
        box-shadow: var(--shadow-md);
        border-color: var(--primary-green);
    }

    .value-icon {
        font-size: ${isMobile ? '2rem' : '2.5rem'};
        color: var(--primary-green);
        margin-bottom: ${isMobile ? '1rem' : '1.25rem'};
    }

    .value-card h3 {
        margin-bottom: ${isMobile ? '0.5rem' : '0.75rem'};
    }

    .value-card p {
        font-size: ${isMobile ? '0.95rem' : '1rem'};
        color: var(--text-light);
        line-height: 1.6;
        margin: 0;
    }

    /* Final CTA */
    .final-cta {
        background: var(--white);
    }

    .cta-content {
        text-align: center;
        max-width: 700px;
        margin: 0 auto;
    }

    .cta-content h2 {
        margin-bottom: ${isMobile ? '0.75rem' : '1rem'};
    }

    .cta-content p {
        font-size: ${isMobile ? '1.063rem' : '1.125rem'};
        margin-bottom: ${isMobile ? '2rem' : '2.5rem'};
        color: var(--text-secondary);
    }

    .cta-actions {
        display: flex;
        gap: ${isMobile ? '0.75rem' : '1rem'};
        justify-content: center;
        margin-bottom: ${isMobile ? '2rem' : '2.5rem'};
        flex-direction: ${isMobile ? 'column' : 'row'};
        align-items: ${isMobile ? 'stretch' : 'center'};
    }

    .final-cta .btn-primary,
    .final-cta .btn-secondary {
        min-width: ${isMobile ? '100%' : '200px'};
    }

    .cta-features {
        display: grid;
        grid-template-columns: ${isMobile ? '1fr' : 'repeat(3, 1fr)'};
        gap: ${isMobile ? '1rem' : '1.5rem'};
        justify-content: center;
    }

    .cta-features .feature {
        display: flex;
        align-items: center;
        justify-content: ${isMobile ? 'flex-start' : 'center'};
        gap: 0.5rem;
        color: var(--primary-green);
        font-weight: 500;
        font-size: ${isMobile ? '0.875rem' : '1rem'};
    }

    .cta-features .feature svg {
        color: var(--primary-green);
        flex-shrink: 0;
    }

    /* SEO Improvements - Schema Markup */
    .solutions-grid {
        itemScope itemType="http://schema.org/ItemList";
    }

    /* Animations */
    @keyframes scroll {
        0% {
            transform: translateY(0);
            opacity: 1;
        }
        100% {
            transform: translateY(20px);
            opacity: 0;
        }
    }

    /* Focus styles for accessibility */
    a:focus,
    button:focus {
        outline: 2px solid var(--primary-green);
        outline-offset: 2px;
    }

    /* Tablet Optimizations */
    @media (min-width: 769px) and (max-width: 1024px) {
        :root {
            --header-height: 75px;
        }
        
        .hero-content {
            flex-direction: column;
        }
        
        .hero-stats {
            width: 100%;
            max-width: 600px;
            margin: 0 auto;
        }
        
        .stats-grid {
            grid-template-columns: repeat(4, 1fr);
        }
        
        .solutions-grid {
            grid-template-columns: repeat(2, 1fr);
        }
        
        .values-grid {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    /* Desktop Optimizations */
    @media (min-width: 1025px) {
        :root {
            --header-height: 80px;
        }
        
        .hero-content {
            flex-direction: row;
        }
        
        .hero-stats {
            width: auto;
        }
        
        .stats-grid {
            grid-template-columns: repeat(2, 1fr);
        }
        
        .impact-grid {
            grid-template-columns: repeat(4, 1fr);
        }
    }

    /* Small Mobile Optimizations */
    @media (max-width: 360px) {
        :root {
            --header-height: 60px;
        }
        
        .container {
            padding: 0 16px;
        }
        
        h1 {
            font-size: 2rem;
        }
        
        h2 {
            font-size: 1.5rem;
        }
        
        .hero-badge {
            font-size: 0.8rem;
            padding: 6px 12px;
        }
        
        .stats-grid {
            grid-template-columns: 1fr;
        }
        
        .impact-grid {
            grid-template-columns: 1fr;
        }
        
        .hero-section {
            padding-top: calc(var(--header-height) + 30px);
        }
    }

    /* High Contrast Mode Support */
    @media (prefers-contrast: high) {
        .btn-primary {
            border: 2px solid white;
        }
        
        .btn-secondary {
            border: 2px solid black;
        }
        
        .solution-card,
        .impact-card,
        .value-card {
            border: 2px solid black;
        }
    }

    /* Reduced Motion */
    @media (prefers-reduced-motion: reduce) {
        * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
        }
        
        .scroll-line::after {
            animation: none;
        }
    }
`}</style>
        </div>
    );
};

export default Home;
